/**
 * "Top picks of the week" panel — the single most important view in the
 * app. Selects between 5 and 10 picks from across the whole 7-day window,
 * sorted by verdict tier then by best EV signal, and tells the user
 * explicitly which number to look at.
 */

import { useMemo } from "react";

import type { PrimaryMetric, ProbabilityPick } from "../../shared/types";
import { formatPercent, formatSignedPercent } from "../lib/format";
import { ConfidenceChip } from "./ConfidenceChip";
import { PickRow } from "./PickRow";
import { VerdictBadge } from "./VerdictBadge";

const MIN_PICKS = 5;
const MAX_PICKS = 10;

export function TopPicks({ picks }: { picks: ProbabilityPick[] }) {
  const top = useMemo(() => {
    return [...picks].sort((a, b) => b.priorityScore - a.priorityScore).slice(0, MAX_PICKS);
  }, [picks]);

  if (top.length === 0) {
    return (
      <section className="panel p-4 text-sm text-ink-500">
        <p className="font-semibold text-ink-900">No picks match the filters this week.</p>
        <p className="mt-1 text-xs">Loosen the league or threshold filters to see more matches.</p>
      </section>
    );
  }

  const target = Math.max(MIN_PICKS, Math.min(MAX_PICKS, top.length));
  const visible = top.slice(0, target);
  const tierCounts = countByVerdict(visible);

  return (
    <section className="space-y-2">
      <header className="panel space-y-1.5 p-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base font-semibold text-ink-900">
            <span aria-hidden>★ </span>
            Bet on {visible.length === MAX_PICKS ? `${MIN_PICKS}–${MAX_PICKS}` : visible.length} of these
            games
          </h2>
          <p className="text-[11px] text-ink-500">7-day window</p>
        </div>
        <p className="text-xs leading-snug text-ink-700">
          These are the most promising picks across the next 7 days, ranked best-first.{" "}
          {tierCounts.best > 0 ? (
            <>
              <strong className="text-emerald-700">{tierCounts.best} ★ BEST</strong> pick
              {tierCounts.best === 1 ? "" : "s"} — model and sharp bookies both agree these are mispriced.
              These are the ones actually worth staking on.{" "}
            </>
          ) : null}
          {tierCounts.good > 0 ? (
            <>
              <strong className="text-emerald-700">{tierCounts.good} 👍 GOOD</strong> — one signal is
              positive; consider these.{" "}
            </>
          ) : null}
          {tierCounts.ok > 0 ? (
            <>
              <strong className="text-ink-700">{tierCounts.ok} ✓ OK</strong> — just smart-favourite picks, no
              edge.{" "}
            </>
          ) : null}
          {tierCounts.avoid > 0 ? (
            <>
              <strong className="text-rose-700">{tierCounts.avoid} ⚠ AVOID</strong>.
            </>
          ) : null}
        </p>
        <p className="text-[11px] leading-snug text-ink-500">
          The big number on each row is the one to look at. <strong>Model EV</strong> / <strong>EV</strong> =
          expected profit per €1 staked, positive is good. When no EV is available we fall back to{" "}
          <strong>Probability</strong> — chance of winning per the no-vig odds.
        </p>
      </header>

      <ol className="space-y-1.5">
        {visible.map((pick, i) => (
          <li key={pick.id} className="flex flex-col gap-1.5">
            <HeadlineRow pick={pick} rank={i + 1} />
            <PickRow pick={pick} rank={i + 1} />
          </li>
        ))}
      </ol>

      {top.length < MIN_PICKS ? (
        <p className="text-[11px] text-ink-500">
          Only {top.length} pick{top.length === 1 ? "" : "s"} meet the filters this week. Lower the min win
          chance or widen the leagues to see more.
        </p>
      ) : null}
    </section>
  );
}

/**
 * Compact "headline" strip above each top pick — verdict + big primary
 * number with its label. Sits above the regular PickRow so the non-expert
 * sees the answer immediately and the engineer-level row below for the
 * supporting detail.
 */
function HeadlineRow({ pick, rank }: { pick: ProbabilityPick; rank: number }) {
  const { value, label, tone } = primaryDisplay(pick);
  const toneClass = tone === "good" ? "text-emerald-700" : tone === "bad" ? "text-rose-700" : "text-ink-900";

  return (
    <div className="panel flex items-center gap-3 px-3 py-2">
      <span className="w-7 shrink-0 text-center text-base font-semibold text-ink-500">#{rank}</span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <VerdictBadge verdict={pick.verdict} />
          <ConfidenceChip confidence={pick.confidence} />
          <p className="truncate text-sm font-semibold text-ink-900">{pick.selectionName}</p>
          <span className="truncate text-[11px] text-ink-500">
            {pick.match.homeTeam} vs {pick.match.awayTeam}
          </span>
        </div>
      </div>
      <div className="shrink-0 text-right">
        <p className={`text-2xl font-semibold leading-tight ${toneClass}`}>{value}</p>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-500">{label}</p>
      </div>
    </div>
  );
}

function primaryDisplay(pick: ProbabilityPick): {
  value: string;
  label: string;
  tone: "good" | "bad" | "neutral";
} {
  const metric: PrimaryMetric = pick.primaryMetric;
  if (metric === "modelEv" && pick.modelEv !== undefined) {
    return {
      value: formatSignedPercent(pick.modelEv),
      label: "Model EV",
      tone: pick.modelEv > 0 ? "good" : pick.modelEv < -0.05 ? "bad" : "neutral",
    };
  }
  if (metric === "evScore" && pick.evScore !== undefined) {
    return {
      value: formatSignedPercent(pick.evScore),
      label: "Consensus EV",
      tone: pick.evScore > 0 ? "good" : pick.evScore < -0.05 ? "bad" : "neutral",
    };
  }
  return {
    value: formatPercent(pick.fairProbability),
    label: "Probability",
    tone: pick.fairProbability >= 0.55 ? "good" : pick.fairProbability < 0.45 ? "bad" : "neutral",
  };
}

function countByVerdict(picks: ProbabilityPick[]): Record<ProbabilityPick["verdict"], number> {
  const out = { best: 0, good: 0, ok: 0, avoid: 0 };
  for (const p of picks) out[p.verdict] += 1;
  return out;
}
